// JS-1149 (DESK-012): the read-only desk-rules panel's PURE presentation.
//
// The owner can now retune the desk's thresholds without a code change, so the
// surfaces have to say which numbers were actually in force for the ideas on
// screen. This module turns the row's own `desk_rules_*` block (see
// services/apis/scripts/run_multi_strategy_screen.py's `desk008_gate_evidence`)
// into a compact line of trader vernacular.
//
// PURE and presentational only -- no fetch, no state, no re-derivation of any
// threshold. Every number here is read verbatim from what the sweep recorded;
// this module only decides how to SAY it.

export type DeskRulesKnobs = Record<string, string | number | boolean>;

export interface DeskRulesPanelSource {
  desk_rules_hash?: string | null;
  desk_rules_version?: string | null;
  desk_rules_source?: string | null;
  desk_rules_knobs?: DeskRulesKnobs | null;
}

export interface DeskRulesPanel {
  /** "Desk rules v1" / "Desk rules (house defaults)". */
  title: string;
  /** The knob line, already in trader vernacular. */
  items: string[];
  /** Short hash for the tooltip/footer, or null when the defaults governed. */
  hashShort: string | null;
  /** Full hash, for the title attribute. */
  hash: string | null;
  /**
   * How many DISTINCT rules identities the displayed ideas were decided under.
   * 1 for a uniform page; more when the list spans a rules activation.
   */
  cohortCount: number;
}

/**
 * Reduce the displayed ideas to ONE rules panel, or report that they disagree.
 *
 * Codex adversarial-review HIGH (round 2): the surfaces originally labelled a
 * whole page from `rows[0]`. That assumption is false — the curated read picks
 * a generation per listing independently and may keep an older priced one, so
 * after a rules activation a single page can legitimately carry two rule
 * hashes. Labelling all of it with the first row's policy tells the trader the
 * ideas were judged by a rule that did not judge some of them.
 *
 * A uniform cohort renders normally. A mixed one renders a panel that SAYS it
 * is mixed and shows no knob values, because no single set of numbers is true
 * of every idea on screen.
 *
 * Codex adversarial-review HIGH (round 3): rows WITHOUT rules evidence used to
 * be filtered out before the cohort comparison, so a page holding one evidenced
 * generation plus some unknown rows saw a single identity and rendered that
 * generation's knob values as page-wide. Absence is not agreement. The tolerant
 * wire schema turns malformed evidence into `undefined`, and pre-ticket rows
 * legitimately carry none at all — either way the honest statement is that this
 * page cannot say what judged every idea on it. It now FAILS CLOSED: any
 * unevidenced row makes the page non-uniform, and the strip names how many rows
 * it cannot account for instead of showing numbers it cannot stand behind.
 */
export function deskRulesPanelForRows(
  rows: ReadonlyArray<{ desk008_gate_evidence?: DeskRulesPanelSource | null }> | null | undefined,
): DeskRulesPanel | null {
  const all = rows ?? [];
  if (all.length === 0) return null;

  const sources: DeskRulesPanelSource[] = [];
  let unknownRows = 0;
  for (const row of all) {
    const source = row?.desk008_gate_evidence;
    const knobs = source?.desk_rules_knobs;
    if (source && knobs && Object.keys(knobs).length > 0) {
      sources.push(source);
    } else {
      unknownRows += 1;
    }
  }
  // Not one row on the page can say what judged it: there is nothing to
  // render, and nothing to warn about either.
  if (sources.length === 0) return null;

  if (unknownRows > 0) {
    return {
      title: `Desk rules · version unknown on ${unknownRows} ${
        unknownRows === 1 ? "row" : "rows"
      }`,
      items: [
        "some of these ideas don't say which rules judged them — open one to see its own",
      ],
      hash: null,
      hashShort: null,
      // The evidenced identities PLUS the unknown cohort, which is a distinct
      // cohort and never folded into an evidenced one.
      cohortCount: identitiesOf(sources).size + 1,
    };
  }

  const identities = identitiesOf(sources);
  const panel = deskRulesPanel(sources[0]);
  if (!panel) return null;
  if (identities.size === 1) return panel;

  return {
    title: `Desk rules · ${identities.size} versions on this page`,
    items: [
      "these ideas were judged under different rules — open one to see its own",
    ],
    hash: null,
    hashShort: null,
    cohortCount: identities.size,
  };
}

/**
 * The DISTINCT rules identities among evidenced rows.
 *
 * Identity is the hash when a document governed. Codex adversarial-review HIGH
 * (round 3): when none did, a single `CODE_DEFAULT` sentinel collapsed every
 * hashless generation into one cohort even when their recorded knob payloads
 * differed — so a page spanning two different code-default generations claimed
 * to be uniform. A hashless cohort is therefore identified by its canonical
 * knob payload, which is the only thing that actually distinguishes one from
 * another.
 */
function identitiesOf(sources: readonly DeskRulesPanelSource[]): Set<string> {
  return new Set(
    sources.map((source) =>
      source.desk_rules_hash
        ? `HASH:${source.desk_rules_hash}`
        : `CODE_DEFAULT:${canonicalKnobs(source.desk_rules_knobs)}`,
    ),
  );
}

/** Key-sorted JSON of a knob block, so payload equality is order-independent. */
function canonicalKnobs(knobs: DeskRulesKnobs | null | undefined): string {
  if (!knobs) return "";
  return JSON.stringify(
    Object.keys(knobs)
      .sort()
      .map((key) => [key, knobs[key]]),
  );
}

/** Render a ratio knob as a percentage when that is how a trader says it. */
function asPercent(value: string | number | boolean | undefined): string | null {
  if (typeof value === "boolean" || value === undefined) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  // Trailing zeros trimmed: "40%" reads better than "40.0%", and the exact
  // string is still available in the raw knob block for anyone who needs it.
  return `${Number((parsed * 100).toFixed(2))}%`;
}

function asRatio(value: string | number | boolean | undefined): string | null {
  if (typeof value === "boolean" || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(value) : null;
}

/**
 * Build the compact read-only panel from a row's recorded rules block.
 *
 * Returns `null` when the row carries no rules block at all -- a row written
 * before this ticket, which genuinely cannot say what judged it. The surfaces
 * render nothing rather than a fabricated "house defaults" claim they have no
 * evidence for.
 */
export function deskRulesPanel(
  source: DeskRulesPanelSource | null | undefined,
): DeskRulesPanel | null {
  if (!source) return null;
  const knobs = source.desk_rules_knobs;
  if (!knobs || Object.keys(knobs).length === 0) return null;

  const hash = source.desk_rules_hash ?? null;
  // A row whose rules came from the code defaults says so plainly. That is an
  // honest statement about the run, not a missing value to hide.
  const version = source.desk_rules_version;
  const title = version ? `Desk rules ${version}` : "Desk rules (house defaults)";

  const items: string[] = [];
  const pop = asPercent(knobs.debit_minimum_pop);
  if (pop) items.push(`PoP floor ${pop}`);

  const cheap = asRatio(knobs.debit_cheap_vol_max_iv_rv_ratio);
  if (cheap) items.push(`cheap vol ≤ ${cheap} IV/RV`);

  const rich = asRatio(knobs.iv_rv_ratio_rich);
  if (rich) items.push(`rich ≥ ${rich}`);

  const indexCeiling = asRatio(knobs.index_debit_max_iv_rv_ratio);
  if (indexCeiling) items.push(`index debit ceiling ${indexCeiling}`);

  const flowHard = asRatio(knobs.flow_contradiction_hard_ratio);
  if (flowHard) items.push(`tape against ${flowHard} stands down`);

  const slipCheap = asPercent(knobs.fill_slippage_max_pct_of_mid_cheap);
  const slipRich = asPercent(knobs.fill_slippage_max_pct_of_mid_rich);
  const slipBoundary = asRatio(knobs.fill_slippage_cheap_premium_boundary);
  if (slipCheap && slipRich && slipBoundary) {
    items.push(`pay through mid ${slipCheap} under $${slipBoundary}, ${slipRich} above`);
  }

  const oi = knobs.structure_liquidity_minimum_leg_open_interest;
  const vol = knobs.structure_liquidity_minimum_leg_volume;
  if (typeof oi === "number" && typeof vol === "number") {
    items.push(`thinnest leg ${oi} OI / ${vol} vol`);
  }

  const earnings = knobs.earnings_veto_days;
  if (typeof earnings === "number") {
    items.push(`no premium sales inside ${earnings} days of a print`);
  }

  return {
    title,
    items,
    hash,
    hashShort: hash ? hash.slice(-8) : null,
    cohortCount: 1,
  };
}
